import { useNavigate } from "react-router-dom";
import { ArrowRight, ImageIcon, Sparkles, UserPlus, Users, Video } from "lucide-react";
import { useCharacters } from "@/hooks";

/**
 * Home-page nudge for the Characters flow. Sits between the prompt
 * input and the community gallery so the feature is unmissable.
 *
 * Two states:
 *  - User has characters → preview their thumbnails + "Keep iterating".
 *  - User has none / not signed in → empty-state with the value prop.
 *
 * Visually we lean louder than a typical card: bright violet→fuchsia
 * gradient border, soft outer glow, gradient backdrop, and a more
 * confident heading. Distinct enough that it pops between the muted
 * prompt input above and the gallery below.
 */
export function CharacterHomeCTA() {
  const navigate = useNavigate();
  const { data } = useCharacters();
  const characters = data?.characters ?? [];
  const hasCharacters = characters.length > 0;
  // Show ONE thumbnail (the latest) + a "+N" badge for the rest, so
  // the card doesn't grow horizontally as the user accumulates more
  // characters. `useCharacters` orders by `latest_at` desc, so
  // `characters[0]` is always the most-recently touched.
  const latest = characters[0] ?? null;
  const remaining = Math.max(0, characters.length - 1);

  return (
    <div className="w-full max-w-2xl mx-auto">
      {/* Soft outer glow — sits behind the card so the gradient feels
          like it lifts off the page. */}
      <div className="relative">
        <div
          aria-hidden
          className="pointer-events-none absolute -inset-1 rounded-3xl bg-gradient-to-r from-violet-500/30 via-fuchsia-500/20 to-purple-500/30 blur-xl opacity-60"
        />

        <button
          type="button"
          onClick={() => navigate("/characters")}
          className="relative w-full text-left rounded-2xl p-[1.5px] bg-gradient-to-r from-violet-500 via-fuchsia-500 to-purple-500 hover:from-violet-400 hover:via-fuchsia-400 hover:to-purple-400 transition-all shadow-2xl shadow-violet-500/20 group"
        >
          <div className="relative flex items-stretch gap-4 md:gap-5 rounded-2xl bg-[#0a0a0a] p-5 md:p-6 overflow-hidden">
            {/* Subtle inner gradient backdrop so the card itself
                isn't pure black — gives the heading more presence. */}
            <div
              aria-hidden
              className="pointer-events-none absolute inset-0 bg-gradient-to-br from-violet-500/[0.06] via-transparent to-fuchsia-500/[0.06]"
            />

            {/* Left visual: the latest character thumbnail + a "+N"
                badge for the rest, so the card stays a fixed width
                no matter how many characters the user has. */}
            <div className="hidden sm:flex shrink-0 items-center relative z-10">
              {hasCharacters && latest ? (
                <div className="flex items-center gap-2">
                  <div
                    className="size-16 rounded-xl overflow-hidden border-2 border-[#0a0a0a] bg-black shadow-lg"
                    title={latest.name}
                  >
                    {latest.canonical_url ? (
                      <img
                        src={latest.canonical_url}
                        alt={latest.name}
                        loading="lazy"
                        className="size-full object-cover"
                      />
                    ) : (
                      <div className="size-full flex items-center justify-center text-[#555]">
                        <Users className="size-5" />
                      </div>
                    )}
                  </div>
                  {remaining > 0 && (
                    <div
                      className="size-10 rounded-full bg-white/[0.06] border border-white/[0.1] text-xs font-semibold text-white/80 flex items-center justify-center shrink-0"
                      title={`${remaining} more character${remaining === 1 ? "" : "s"}`}
                    >
                      +{remaining}
                    </div>
                  )}
                </div>
              ) : (
                <div className="size-16 rounded-2xl bg-gradient-to-br from-violet-500/30 to-fuchsia-500/30 border border-white/[0.15] flex items-center justify-center shadow-lg shadow-violet-500/10">
                  <UserPlus className="size-7 text-white" />
                </div>
              )}
            </div>

            {/* Copy block */}
            <div className="flex-1 min-w-0 flex flex-col justify-center relative z-10">
              <div className="flex items-center gap-2 mb-1.5">
                <Sparkles className="size-3.5 text-fuchsia-300" />
                <span className="text-[11px] uppercase tracking-wider font-semibold text-fuchsia-300">
                  Characters
                </span>
              </div>
              <h3 className="text-base sm:text-lg md:text-xl font-bold text-white leading-tight">
                {hasCharacters
                  ? "Keep iterating on your characters"
                  : "One character. Endless images and videos."}
              </h3>
              <p className="text-sm text-[#aaa] mt-1.5 leading-relaxed">
                {hasCharacters
                  ? "Generate them in new scenes, tweak the look, animate any version."
                  : "Save a character once. Re-use them in any image edit and animate them in any video."}
              </p>

              {/* Feature row — small hierarchy of what the flow gives you. */}
              <div className="hidden md:flex items-center gap-4 mt-3 text-xs text-[#888]">
                <span className="flex items-center gap-1.5">
                  <UserPlus className="size-3.5 text-violet-300" />
                  Save once
                </span>
                <span className="flex items-center gap-1.5">
                  <ImageIcon className="size-3.5 text-violet-300" />
                  Edit any scene
                </span>
                <span className="flex items-center gap-1.5">
                  <Video className="size-3.5 text-violet-300" />
                  Animate any look
                </span>
              </div>
            </div>

            {/* CTA pill */}
            <div className="shrink-0 self-center flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white text-black text-sm font-semibold shadow-lg group-hover:scale-[1.03] group-active:scale-[0.98] transition-transform relative z-10">
              {hasCharacters ? "Open" : "Create"}
              <ArrowRight className="size-4" />
            </div>
          </div>
        </button>
      </div>
    </div>
  );
}
